import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    MinusCircleIcon,
    ArrowPathIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { deleteUserFantasyTeams } from '../api/fantasyteams';
import { toast } from 'react-hot-toast';

const LoadingSpinner = ({ size = 'md' }) => <span className={`loading loading-spinner loading-${size} text-primary`}></span>;

const TeamFilterCard = ({
    userTeams,
    isLoadingTeams,
    selectedTeamIds,
    isAllTeamsSelected,
    onTeamSelectionChange,
    onSelectAllTeams,
    onSelectNoneTeams,
    onResyncTeam,
    isResyncing,
    teamToResync,
    onTeamsDeletedSuccessfully,
    isMobile = false,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [teamsToDeleteInModal, setTeamsToDeleteInModal] = useState(new Set());
    const [isDeletingInModal, setIsDeletingInModal] = useState(false);
    const [modalError, setModalError] = useState(null);

    const modalRef = useRef(null);

    useEffect(() => {
        if (!modalRef.current) return;
        if (isModalOpen) {
            modalRef.current.showModal();
        } else {
            if (modalRef.current.open) {
                modalRef.current.close();
            }
        }
    }, [isModalOpen]);

    const handleOpenModal = () => {
        const validSelectedTeamIds = new Set();
        const currentTeamIds = new Set(userTeams.map(t => t.id));
        selectedTeamIds.forEach(id => {
            if (currentTeamIds.has(id)) {
                validSelectedTeamIds.add(id);
            }
        });
        setTeamsToDeleteInModal(validSelectedTeamIds);
        setModalError(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const toggleTeamForDeletionInModal = (teamId) => {
        setTeamsToDeleteInModal(prev => {
            const newSet = new Set(prev);
            if (newSet.has(teamId)) {
                newSet.delete(teamId);
            } else {
                newSet.add(teamId);
            }
            return newSet;
        });
    };

    const handleConfirmDeletion = async () => {
        if (teamsToDeleteInModal.size === 0) {
            setModalError("Please select at least one team to delete.");
            return;
        }
        setIsDeletingInModal(true);
        setModalError(null);
        const toastId = toast.loading('Deleting team(s)...');

        try {
            const idsToDelete = Array.from(teamsToDeleteInModal);
            await deleteUserFantasyTeams(idsToDelete);
            toast.success(`Successfully deleted ${idsToDelete.length} team(s).`, { id: toastId });
            
            setTeamsToDeleteInModal(new Set());
            setIsModalOpen(false);

            if (onTeamsDeletedSuccessfully) {
                onTeamsDeletedSuccessfully();
            }
        } catch (err) {
            console.error("Error deleting teams from modal:", err);
            const errorMessage = err.message || "Failed to delete teams. Please try again.";
            setModalError(errorMessage);
            toast.error(errorMessage, { id: toastId });
        } finally {
            setIsDeletingInModal(false);
        }
    };

    return (
        <>
            <div className={`card bg-base-100 shadow-md p-4 ${isMobile ? 'h-60 flex flex-col' : ''}`}>
                <div className={`flex justify-between items-center border-b pb-2 mb-3 ${isMobile ? 'flex-shrink-0' : ''}`}>
                    <h3 className="text-md md:text-lg font-semibold">Filter by Team</h3>
                    {userTeams.length > 0 && (
                        <button 
                            onClick={handleOpenModal} 
                            className="btn btn-outline btn-error btn-xs rounded-full p-1.5 min-h-0 h-auto leading-none flex items-center"
                            title="Manage Teams"
                            disabled={isLoadingTeams || isResyncing}
                        >
                            <MinusCircleIcon className="w-4 h-4" /> 
                            <span className={`ml-1 text-xs ${isMobile ? 'hidden xs:inline' : 'sm:inline'}`}>Manage</span>
                        </button>
                    )}
                </div>
                {isLoadingTeams ? (
                    <div className={`h-full flex items-center justify-center ${isMobile ? 'flex-grow' : ''}`}><LoadingSpinner size="sm"/></div>
                ) : userTeams.length === 0 ? (
                    <p className={`text-sm text-base-content/70 italic ${isMobile ? 'h-full flex items-center justify-center flex-grow' : ''}`}>No teams. <Link to="/addteam" className="link link-secondary ml-1">Add one!</Link></p>
                ) : (
                    <div className={`space-y-1 overflow-y-auto pr-2 ${isMobile ? 'flex-grow min-h-0' : 'max-h-60'}`}>
                        <div className="form-control">
                            <label className="label cursor-pointer py-1 justify-start gap-2">
                                <input 
                                    type="checkbox" 
                                    className="checkbox checkbox-sm checkbox-secondary" 
                                    checked={isAllTeamsSelected}
                                    onChange={isAllTeamsSelected ? onSelectNoneTeams : onSelectAllTeams} 
                                    disabled={isLoadingTeams || isResyncing}
                                />
                                <span className="label-text text-sm font-medium">All Teams</span>
                            </label>
                        </div>
                        {userTeams.map(team => {
                            const isCurrentTeamResyncing = isResyncing && teamToResync === team.id;
                            const isDisabled = isLoadingTeams || isResyncing;

                            return (
                                <li 
                                    key={team.id} 
                                    className={`p-2.5 rounded-lg cursor-pointer transition-all flex items-center justify-between group 
                                                ${selectedTeamIds.has(team.id) ? 'bg-primary text-primary-content shadow-md' : 'bg-base-200 hover:bg-base-300'}
                                                ${isDisabled ? 'opacity-60 pointer-events-none' : ''}`}
                                    onClick={() => !isDisabled && onTeamSelectionChange(team.id)}
                                >
                                    <div className="truncate flex-grow mr-2">
                                        <p className="font-medium text-sm truncate" title={team.team_name}>{team.team_name || "Unnamed Team"}</p>
                                        <p className="text-xs opacity-70 truncate" title={team.league_name}>{team.league_name || "N/A"} ({team.league_type})</p>
                                    </div>
                                    <div className="flex items-center flex-shrink-0 gap-1">
                                        {team.league_type !== 'manual' && (
                                            <button
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (!isDisabled) onResyncTeam(team.id); 
                                                }}
                                                className={`btn btn-xs btn-ghost p-1 ${isCurrentTeamResyncing ? 'loading' : (selectedTeamIds.has(team.id) ? 'text-primary-content/80 hover:text-primary-content' : 'text-base-content/60 hover:text-base-content/90')}`}
                                                disabled={isDisabled || (isResyncing && teamToResync !== team.id)}
                                                title="Re-sync this team"
                                            >
                                                {isCurrentTeamResyncing ? 
                                                    <span className="loading loading-spinner loading-xs"></span> : 
                                                    <ArrowPathIcon className="h-4 w-4" />
                                                }
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </div>
                )}
            </div>

            <dialog ref={modalRef} className="modal modal-bottom sm:modal-middle">
                <div className="modal-box">
                    <h3 className="font-bold text-lg mb-4">Manage & Remove Teams</h3>
                    <button onClick={handleCloseModal} className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
                    
                    {modalError && <p className="text-error text-sm mb-3">{modalError}</p>}

                    {userTeams.length === 0 ? (
                        <p>You don't have any teams to manage.</p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto mb-4 pr-1">
                            {userTeams.map(team => (
                                <div key={team.id} className={`p-2 rounded-md flex items-center justify-between ${teamsToDeleteInModal.has(team.id) ? 'bg-error/20' : 'bg-base-200'}`}>
                                    <div>
                                        <p className="text-sm font-medium">{team.team_name || "Unnamed Team"}</p>
                                        <p className="text-xs text-base-content/70">{team.league_name || "N/A"} ({team.league_type})</p>
                                    </div>
                                    <input 
                                        type="checkbox"
                                        className="checkbox checkbox-error checkbox-sm"
                                        checked={teamsToDeleteInModal.has(team.id)}
                                        onChange={() => toggleTeamForDeletionInModal(team.id)}
                                        disabled={isDeletingInModal}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="modal-action">
                        <button onClick={handleCloseModal} className="btn btn-ghost" disabled={isDeletingInModal}>Cancel</button>
                        <button 
                            onClick={handleConfirmDeletion} 
                            className={`btn btn-error ${isDeletingInModal ? 'btn-disabled' : ''}`} 
                            disabled={isDeletingInModal || teamsToDeleteInModal.size === 0}
                        >
                            {isDeletingInModal ? <LoadingSpinner size="xs" /> : <TrashIcon className="w-4 h-4 mr-2" />} 
                            Delete ({teamsToDeleteInModal.size}) Team(s)
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </>
    );
}

export default TeamFilterCard; 